import { api } from "@/app/app.api";
import { RequestMethod } from "@/shared/utils/RequestMethod";
import {
  GetTrainingNutritionsResponse,
  Trainer,
  TransformedResponse,
} from "./kpi.types";

export interface CatchmentRequest {
  branch_filter?: number | null;
  catchment_page: string;
  sex_filter?: "male" | "female" | null;
  campaign_filter?: number | null;
  search?: string | null;
  stages_filter?: string | null;
  stages_users_filter?: string[] | null;
  tag_marketing_filter?: string[] | null;
}

export const kpiApi = api
  .enhanceEndpoints({ addTagTypes: ["kpi"] })
  .injectEndpoints({
    overrideExisting: false,
    endpoints: (builder) => ({
      ChartBranches: builder.query<TransformedResponse, void>({
        query: () => "kpi/chartBranches",
        transformResponse: (response: any): TransformedResponse => {
          let total = 0;
          const transformedData = response.map((item: any) => {
            total += item.total;
            return {
              category: item.branch,
              value: item.total,
            };
          });
          return { transformedData, total };
        },
      }),
      chartsUsersPerMonth: builder.query({
        query: ({ filial, year }) => {
          let url = `kpi/chartsUsersPerMonth`;
          if (filial) {
            url += `?filial=${filial}&year=${year}`;
          }
          return {
            url: url,
            method: RequestMethod.GET,
          };
        },
      }),
      chartsUsersAge: builder.query({
        query: ({ filial }) => {
          let url = `kpi/chartsUsersAge`;
          if (filial) {
            url += `?filial=${filial}`;
          }
          return {
            url: url,
            method: RequestMethod.GET,
          };
        },
      }),
      getEarningsTotalBranches: builder.query({
        query: ({ currency_request, filial, year }) => {
          return {
            url: `kpi/getEarningsTotalBranches`,
            method: RequestMethod.GET,
            params: { currency_request, filial, year },
          };
        },
      }),
      getEarningsTotalBranchByDays: builder.query({
        query: ({ currency_request, filial, month, year }) => {
          return {
            url: `kpi/getEarningsTotalBranchByDays`,
            method: RequestMethod.GET,
            params: { currency_request, filial, month, year },
          };
        },
      }),
      getEarningsTotalBranchSource: builder.query({
        query: ({ currency_request, filial, year }) => {
          return {
            url: `kpi/getEarningsTotalBranchSource`,
            method: RequestMethod.GET,
            params: { currency_request, filial, year },
          };
        },
      }),
      getEarningsTotalBranchByDaysSource: builder.query({
        query: ({ currency_request, filial, month, year }) => {
          return {
            url: `kpi/getEarningsTotalBranchByDaysSource`,
            method: RequestMethod.GET,
            params: { currency_request, filial, month, year },
          };
        },
      }),
      getEarningsBranch: builder.query({
        query: ({ currency_request, filial, year }) => {
          return {
            url: `kpi/getEarningsBranch`,
            method: RequestMethod.GET,
            params: { currency_request, filial, year },
          };
        },
      }),
      getEarningsBranchByDays: builder.query({
        query: ({ currency_request, filial, month, year }) => {
          return {
            url: `kpi/getEarningsBranchByDays`,
            method: RequestMethod.GET,
            params: { currency_request, filial, month, year },
          };
        },
      }),
      getEarningsStoreBranch: builder.query({
        query: ({ currency_request, filial, year }) => {
          return {
            url: `kpi/getEarningsStoreBranch`,
            method: RequestMethod.GET,
            params: { currency_request, filial, year },
          };
        },
      }),
      getEarningsStoreBranchByDays: builder.query({
        query: ({ currency_request, filial, month, year }) => {
          return {
            url: `kpi/getEarningsStoreBranchByDays`,
            method: RequestMethod.GET,
            params: { currency_request, filial, month, year },
          };
        },
      }),
      getEarningsStoreCategoryBranch: builder.query({
        query: ({ currency_request, filial, year }) => {
          return {
            url: `kpi/getEarningsStoreCategoryBranch`,
            method: RequestMethod.GET,
            params: { currency_request, filial, year },
          };
        },
      }),
      getEarningsStoreBranchCategoryByDays: builder.query({
        query: ({ currency_request, filial, year }) => {
          return {
            url: `kpi/getEarningsStoreBranchCategoryByDays`,
            method: RequestMethod.GET,
            params: { currency_request, filial, year },
          };
        },
      }),
      getEarningsSPBonds: builder.query({
        query: ({ currency_request, filial, year }) => {
          return {
            url: `kpi/getEarningsSPBonds`,
            method: RequestMethod.GET,
            params: { currency_request, filial, year },
          };
        },
      }),
      getEarningsSPBondsBranchByDays: builder.query({
        query: ({ currency_request, filial, month, year }) => {
          return {
            url: `kpi/getEarningsSPBondsBranchByDays`,
            method: RequestMethod.GET,
            params: { currency_request, filial, month, year },
          };
        },
      }),
      getEarningsBonds: builder.query({
        query: ({ currency_request, filial, year }) => {
          return {
            url: `kpi/getEarningsBonds`,
            method: RequestMethod.GET,
            params: { currency_request, filial, year },
          };
        },
      }),
      getEarningsBondsBranchByDays: builder.query({
        query: ({ currency_request, filial, month, year }) => {
          return {
            url: `kpi/getEarningsBondsBranchByDays`,
            method: RequestMethod.GET,
            params: { currency_request, filial, month, year },
          };
        },
      }),
      getEarningsFreeCourses: builder.query({
        query: ({ currency_request, filial, year }) => {
          return {
            url: `kpi/getEarningsFreeCourses`,
            method: RequestMethod.GET,
            params: { currency_request, filial, year },
          };
        },
      }),
      getEarningsFreeCoursesBranchByDays: builder.query({
        query: ({ currency_request, filial, month, year }) => {
          return {
            url: `kpi/getEarningsFreeCoursesBranchByDays`,
            method: RequestMethod.GET,
            params: { currency_request, filial, month, year },
          };
        },
      }),
      getExpensesBranch: builder.query({
        query: ({ currency_request, filial, year }) => {
          return {
            url: `kpi/getExpensesBranch`,
            method: RequestMethod.GET,
            params: { currency_request, filial, year },
          };
        },
      }),
      getExpensesBranchByDays: builder.query({
        query: ({ currency_request, filial, month, year }) => {
          return {
            url: `kpi/getExpensesBranchByDays`,
            method: RequestMethod.GET,
            params: { currency_request, filial, month, year },
          };
        },
      }),
      getExpensesCategoryBranch: builder.query({
        query: ({ currency_request, category_request, filial, year }) => {
          return {
            url: `kpi/getExpensesCategoryBranch`,
            method: RequestMethod.GET,
            params: { currency_request, category_request, filial, year },
          };
        },
      }),
      getExpensesCategoryBranchByDays: builder.query({
        query: ({
          currency_request,
          category_request,
          filial,
          month,
          year,
        }) => {
          return {
            url: `kpi/getExpensesCategoryBranchByDays`,
            method: RequestMethod.GET,
            params: { currency_request, category_request, filial, month, year },
          };
        },
      }),
      getBenefits: builder.query({
        query: ({ currency_request, filial, year }) => {
          return {
            url: `kpi/getBenefits`,
            method: RequestMethod.GET,
            params: { currency_request, filial, year },
          };
        },
      }),
      getBenefitsByDays: builder.query({
        query: ({ currency_request, filial, month, year }) => {
          return {
            url: `kpi/getBenefitsByDays`,
            method: RequestMethod.GET,
            params: { currency_request, filial, month, year },
          };
        },
      }),
      getStatusClients: builder.query({
        query: ({ age_min, age_max, sex, currency_request, revenue_source }) => {
          return {
            url: `kpi/statusClients`,
            method: RequestMethod.GET,
            params: {
              age_min,
              age_max,
              sex,
              currency_request,
              revenue_source,
            },
          };
        },
      }),
      getInactiveUsersForMonth: builder.query({
        query: () => {
          return {
            url: `kpi/getInactiveUsersForMonth`,
            method: RequestMethod.GET,
          };
        },
      }),
      getNewUsersForMonth: builder.query({
        query: () => {
          return {
            url: `kpi/getNewUsersForMonth`,
            method: RequestMethod.GET,
          };
        },
      }),
      getUserLess: builder.query({
        query: () => {
          return {
            url: `kpi/getUserLess`,
            method: RequestMethod.GET,
          };
        },
      }),
      getAccessBranch: builder.query({
        query: () => {
          return {
            url: `kpi/getAccessBranch`,
            method: RequestMethod.GET,
          };
        },
      }),
      getUtilization: builder.query({
        query: () => {
          return {
            url: `kpi/getUtilization`,
            method: RequestMethod.GET,
          };
        },
      }),
      getTrafficLights: builder.query({
        query: () => {
          return {
            url: `kpi/getTrafficLights`,
            method: RequestMethod.GET,
          };
        },
      }),
      getUtilizationChart3: builder.query({
        query: () => {
          return {
            url: `kpi/getUtilizationChart3`,
            method: RequestMethod.GET,
          };
        },
      }),
      getLessonUtilizationByDays: builder.query({
        query: ({ day }) => {
          return {
            url: `kpi/getLessonUtilizationByDays`,
            method: RequestMethod.GET,
            params: { day },
          };
        },
      }),
      getReactions: builder.mutation({
        query: (values) => {
          console.log(values, "values");
          return {
            url: `kpi/getReactions`,
            method: RequestMethod.POST,
            body: values,
          };
        },
      }),
      getFiltersReactions: builder.query({
        query: ({ day }) => {
          return {
            url: `kpi/getFiltersReactions`,
            method: RequestMethod.GET,
            params: { day },
          };
        },
      }),
      getTrainingNutrition: builder.query({
        query: () => {
          return {
            url: `kpi/getTrainingNutritions`,
            method: RequestMethod.GET,
          };
        },
        transformResponse: (response: any) => {
          return response.trainers.map((trainer: Trainer) => ({
            id: trainer.id,
            name: trainer.name,
            avatar: trainer.avatar,
          }));
        },
      }),
      trainingNutritionExport: builder.mutation({
        query: (catchmentRequest: CatchmentRequest) => {
          return {
            url: `admin/catchment/export`,
            method: "POST",
            body: {
              ...catchmentRequest,
            },
            responseHandler: (response) => response.blob(), // Maneja la respuesta como un blob
          };
        },
      }),
      getRecalculatePlan: builder.query({
        query: ({ date_start, date_end, coach }) => {
          return {
            url: `kpi/getRecalculatePlan`,
            method: RequestMethod.GET,
            params: { date_start, date_end, coach },
          };
        },
      }),
      getAbsences: builder.query({
        query: ({ currency_request, filial, month, year }) => {
          return {
            url: `kpi/absences`,
            method: RequestMethod.GET,
            params: { currency_request, filial, month, year },
          };
        },
      }),
      getAbsencesLesson: builder.query({
        query: ({ currency_request, filial, month, year }) => {
          return {
            url: `kpi/absencesLesson`,
            method: RequestMethod.GET,
            params: { currency_request, filial, month, year },
          };
        },
      }),
      getAbsencesProgram: builder.query({
        query: ({ currency_request, filial, month, year }) => {
          return {
            url: `kpi/absencesProgram`,
            method: RequestMethod.GET,
            params: { currency_request, filial, month, year },
          };
        },
      }),
    }),
  });

export const {
  useChartBranchesQuery,
  useChartsUsersPerMonthQuery,
  useChartsUsersAgeQuery,
  useGetEarningsTotalBranchesQuery,
  useGetEarningsTotalBranchByDaysQuery,
  useGetEarningsTotalBranchSourceQuery,
  useGetEarningsTotalBranchByDaysSourceQuery,
  useGetEarningsBranchQuery,
  useGetEarningsBranchByDaysQuery,
  useGetEarningsStoreBranchQuery,
  useGetEarningsStoreBranchByDaysQuery,
  useGetEarningsStoreCategoryBranchQuery,
  useGetEarningsStoreBranchCategoryByDaysQuery,
  useGetEarningsSPBondsQuery,
  useGetEarningsSPBondsBranchByDaysQuery,
  useGetEarningsBondsQuery,
  useGetEarningsBondsBranchByDaysQuery,
  useGetEarningsFreeCoursesQuery,
  useGetEarningsFreeCoursesBranchByDaysQuery,
  useGetExpensesBranchQuery,
  useGetExpensesBranchByDaysQuery,
  useGetExpensesCategoryBranchQuery,
  useGetExpensesCategoryBranchByDaysQuery,
  useGetBenefitsQuery,
  useGetBenefitsByDaysQuery,
  useGetStatusClientsQuery,
  useGetInactiveUsersForMonthQuery,
  useGetNewUsersForMonthQuery,
  useGetUserLessQuery,
  useGetAccessBranchQuery,
  useGetUtilizationQuery,
  useGetTrafficLightsQuery,
  useGetUtilizationChart3Query,
  useGetLessonUtilizationByDaysQuery,
  useGetReactionsMutation,
  useGetFiltersReactionsQuery,
  useGetTrainingNutritionQuery,
  useTrainingNutritionExportMutation,
  useGetRecalculatePlanQuery,
  useGetAbsencesQuery,
  useGetAbsencesLessonQuery,
  useGetAbsencesProgramQuery,
} = kpiApi;
